import { useTranslation } from 'react-i18next';
import {
    AlertDialog,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Text,
} from 'shell/components';

const QuizSubmissionDialogs = ({
    foundSubmissions,
    confirmationOpen,
    previewOpen,
    submitting,
    onConfirmImport,
    onCloseImportDialogs,
    onStartProcessing,
}) => {
    const { t } = useTranslation('quiz');
    const hasSubmissions = foundSubmissions.length > 0;

    return (
        <>
            <AlertDialog
                open={confirmationOpen}
                message={
                    hasSubmissions
                        ? t('submissions.confirm-title', 'Import Submissions?')
                        : t('submissions.empty-title', 'No Submissions Found')
                }
                description={
                    hasSubmissions
                        ? t(
                              'submissions.confirm-description',
                              'We found ({{count}}) submissions to import for this quiz. Do you want to import them now?',
                              { count: foundSubmissions.length }
                          )
                        : t('submissions.empty-description', 'There are no new completed quiz submissions to import.')
                }
                note={
                    hasSubmissions
                        ? t(
                              'submissions.confirm-note',
                              'Note: Once you import, TAs can view and grade submissions for their assigned sections.'
                          )
                        : ''
                }
                actionButtonText={
                    hasSubmissions ? t('common:controls.import', 'Import') : t('common:controls.ok', 'Ok')
                }
                cancelButtonText={hasSubmissions ? t('common:controls.cancel', 'Cancel') : ''}
                onAction={onConfirmImport}
                onCancel={onCloseImportDialogs}
                onClose={onCloseImportDialogs}
            />
            <Dialog
                open={previewOpen}
                scroll={'paper'}
                aria-labelledby={'quiz-submission-import-title'}
                onClose={onCloseImportDialogs}
                sx={{
                    '& .MuiDialog-paper': {
                        width: 540,
                        maxWidth: 'calc(100% - 32px)',
                        height: 632,
                        maxHeight: 'calc(100% - 32px)',
                        m: 4,
                    },
                }}
            >
                <DialogTitle id={'quiz-submission-import-title'}>
                    <Stack spacing={1}>
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} spacing={3}>
                            <Text component={'h2'} variant={'h6'} weight={'medium'}>
                                {t('submissions.preview-title', 'Import Quizzes')}
                            </Text>
                            <IconButton
                                aria-label={t('common:controls.close', 'Close')}
                                kind={'exit'}
                                size={'medium'}
                                variant={'square'}
                                disabled={submitting}
                                onClick={onCloseImportDialogs}
                            />
                        </Stack>
                        <Text>
                            {t('submissions.preview-description', 'We found ({{count}}) completed quizzes to import.', {
                                count: foundSubmissions.length,
                            })}
                        </Text>
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Stack
                        component={'ul'}
                        aria-label={t('submissions.list-label', 'Quiz submissions')}
                        spacing={3}
                        sx={{ listStyle: 'none', m: 0, p: 0 }}
                    >
                        {foundSubmissions.map((submission, index) => {
                            const user = submission.api.user;
                            const sections = user.sections || (user.section ? [user.section] : []);
                            const sectionNames = sections.map((section) =>
                                typeof section === 'object' ? section.name || section.id : section
                            );
                            return (
                                <Stack
                                    component={'li'}
                                    key={`${submission.api.id}-${index}`}
                                    sx={(theme) => ({
                                        p: 3,
                                        border: `1px solid ${theme.palette.ensightful.neutral[100]}`,
                                        borderRadius: '8px',
                                        background: theme.palette.ensightful.neutral[50],
                                    })}
                                >
                                    <Text sx={{ overflowWrap: 'anywhere' }}>
                                        {user.name || t('submissions.unknown-student', 'Student')} (
                                        {user.identifier || user.id})
                                    </Text>
                                    <Text color={'light'}>
                                        {t('submissions.section', 'Section: {{section}}', {
                                            section:
                                                sectionNames.join(', ') ||
                                                t('submissions.unknown-section', 'Not available'),
                                        })}
                                    </Text>
                                </Stack>
                            );
                        })}
                    </Stack>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button
                        loading={submitting}
                        loadingGrow={submitting}
                        disabled={!hasSubmissions}
                        onClick={onStartProcessing}
                    >
                        {t('submissions.start-processing', 'Start Processing')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default QuizSubmissionDialogs;
